import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import { ButtonModule, IconModule } from '@hglobal/common';

import { TranslateInPipe } from '../../../../pipes/translate-in.pipe';
import { CTA_DISPLAY_DELAY_MS } from '../../cta.constants';
import { CtaDefinition, CtaSurface } from '../../cta.models';
import { CtaStore } from '../../cta.store';
import { PageContextService } from '../../page-context.service';
import { CtaListComponent } from '../cta-list/cta-list.component';

@Component({
  selector: 'fb-page-cta',
  templateUrl: './page-cta.component.html',
  styleUrl: './page-cta.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonModule, IconModule, CtaListComponent, TranslateInPipe],
  providers: [CtaStore],
})
export class PageCtaComponent {
  readonly CtaSurface = CtaSurface;
  readonly #store = inject(CtaStore);
  readonly #pageContext = inject(PageContextService);
  readonly #destroyRef = inject(DestroyRef);

  /**
   * Hidden while the FAB action menu or a widget dialog is shown. Host-page
   * media/overlays (modal dialogs, full-screen players/viewers, fullscreen
   * video) need no explicit handling: the widget renders inside the
   * `floating` z-index layer, so any overlay that covers the FAB covers the
   * CTA too — the CTA shares the FAB's visibility by construction.
   */
  readonly isSuppressed = input<boolean>(false);

  readonly ctaSelected = output<CtaDefinition>();

  readonly ctas = this.#store.visibleCtas;

  readonly #closedByUser = signal(false);
  readonly #isRevealReady = signal(false);

  readonly isVisible = computed<boolean>(
    () => this.#store.isSupportedContext()
      && !this.isSuppressed()
      && !this.#closedByUser()
      && this.#isRevealReady()
      && this.ctas().length > 0,
  );

  #revealTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.#store.setSurface(CtaSurface.Page);

    effect(
      () => {
        const path = this.#pageContext.currentPath();
        untracked(() => {
          this.#store.syncCooldowns();
          this.#store.setCurrentPath(path);
          this.#closedByUser.set(false);
          this.#scheduleReveal();
        });
      },
      { allowSignalWrites: true },
    );

    this.#destroyRef.onDestroy(() => this.#clearReveal());
  }

  close(): void {
    this.#closedByUser.set(true);
  }

  #scheduleReveal(): void {
    this.#clearReveal();
    this.#isRevealReady.set(false);
    this.#revealTimer = setTimeout(
      () => this.#isRevealReady.set(true),
      CTA_DISPLAY_DELAY_MS,
    );
  }

  #clearReveal(): void {
    if (this.#revealTimer !== null) {
      clearTimeout(this.#revealTimer);
      this.#revealTimer = null;
    }
  }
}
